const PRINTABLE_CONDITIONS_HEIGHT_MM = 198 - 30 - 13 - 18 - 6;
const CONDITIONS_LINE_HEIGHT_MM = 3.5;
// The print stylesheet renders a 140mm-wide page. Keep the estimate deliberately
// conservative so browser wrapping cannot push a conditions block below the footer.
const CONDITIONS_CHARS_PER_LINE = 55;
const CONDITIONS_LINES_PER_PAGE = Math.floor(PRINTABLE_CONDITIONS_HEIGHT_MM / CONDITIONS_LINE_HEIGHT_MM);
const CONDITIONS_CHARS_PER_PAGE = CONDITIONS_LINES_PER_PAGE * CONDITIONS_CHARS_PER_LINE;

export type BudgetConditionsPaginationOptions = {
  initialLinesCapacity?: number;
};

export type ConditionsPageKind = 'standard';

export type MeasureConditionsFragment = (html: string, pageKind: ConditionsPageKind) => Promise<boolean>;

export function paginateBudgetConditions(
  saleOrderTerms: string | null | undefined,
  companyTerms: string | null | undefined,
  options: BudgetConditionsPaginationOptions = {}
): string[] {
  const conditions = getUsableTerms(saleOrderTerms) || getUsableTerms(companyTerms);
  if (!conditions) {
    return [];
  }

  const initialLinesCapacity = getInitialLinesCapacity(options.initialLinesCapacity);

  const container = document.createElement('div');
  container.innerHTML = conditions;

  if (container.children.length === 0) {
    return splitPlainText(conditions, initialLinesCapacity);
  }

  return splitHtmlElements(Array.from(container.children), initialLinesCapacity);
}

/**
 * Pagina condiciones contra el alto real que informa el componente. La heurística
 * existente queda como fallback determinista cuando el DOM no puede medirse.
 */
export async function paginateBudgetConditionsAsync(
  saleOrderTerms: string | null | undefined,
  companyTerms: string | null | undefined,
  measure: MeasureConditionsFragment
): Promise<string[]> {
  const conditions = getUsableTerms(saleOrderTerms) || getUsableTerms(companyTerms);
  if (!conditions || typeof measure !== 'function') {
    return conditions ? paginateBudgetConditions(conditions, null) : [];
  }

  const container = document.createElement('div');
  container.innerHTML = conditions;

  if (container.children.length === 0) {
    return paginateBudgetConditions(conditions, null);
  }

  const measuredPages = await paginateHtmlElementsByMeasurement(Array.from(container.children), measure);
  return measuredPages ?? paginateBudgetConditions(conditions, null);
}

async function paginateHtmlElementsByMeasurement(
  elements: Element[],
  measure: MeasureConditionsFragment
): Promise<string[] | null> {
  const pages: string[] = [];
  let currentPage = '';

  const pageKind = (): ConditionsPageKind => 'standard';
  const pushCurrentPage = (): void => {
    if (getVisibleText(currentPage).trim()) {
      pages.push(currentPage);
    }
    currentPage = '';
  };

  const fits = async (html: string): Promise<boolean | null> => {
    try {
      const result = await measure(html, pageKind());
      return typeof result === 'boolean' ? result : null;
    } catch {
      return null;
    }
  };

  const appendMeasuredLongListItem = async (list: Element, listItem: Element, listItemNumber: number): Promise<boolean> => {
    const boundaries = getWordBoundaries(listItem);
    if (boundaries.length === 0) {
      return false;
    }

    let nextWordIndex = 0;
    while (nextWordIndex < boundaries.length) {
      const fragment = await findLargestMeasuredFragment(
        listItem,
        boundaries,
        nextWordIndex,
        currentPage,
        fits,
        fragmentHtml => serializeListPage(list, fragmentHtml, listItemNumber)
      );
      if (fragment === null) {
        return false;
      }
      if (!fragment) {
        if (!currentPage) {
          return false;
        }
        pushCurrentPage();
        continue;
      }

      currentPage += fragment.html;
      nextWordIndex = fragment.nextWordIndex;
      if (nextWordIndex < boundaries.length) {
        pushCurrentPage();
      }
    }

    return true;
  };

  const appendMeasuredList = async (list: Element): Promise<boolean> => {
    const listItems = Array.from(list.children).filter(child => child.tagName.toLowerCase() === 'li');
    if (listItems.length === 0) {
      return false;
    }

    let currentItems: string[] = [];
    let currentListStart = getListStart(list);
    let nextListNumber = getListStart(list);
    let listItemIndex = 0;

    while (listItemIndex < listItems.length) {
      const listItem = listItems[listItemIndex];
      const listItemNumber = getListItemNumber(listItem, nextListNumber);
      const candidateItems = [...currentItems, listItem.outerHTML];
      const candidateList = serializeListPage(list, candidateItems.join(''), currentListStart);
      const candidateFits = await fits(`${currentPage}${candidateList}`);
      if (candidateFits === null) {
        return false;
      }

      if (candidateFits) {
        currentItems = candidateItems;
        nextListNumber = listItemNumber + 1;
        listItemIndex += 1;
        continue;
      }

      if (currentItems.length > 0) {
        currentPage += serializeListPage(list, currentItems.join(''), currentListStart);
        pushCurrentPage();
        currentItems = [];
        currentListStart = listItemNumber;
        continue;
      }

      if (currentPage) {
        pushCurrentPage();
        continue;
      }

      if (!await appendMeasuredLongListItem(list, listItem, listItemNumber)) {
        return false;
      }

      nextListNumber = listItemNumber + 1;
      currentListStart = nextListNumber;
      listItemIndex += 1;
    }

    if (currentItems.length > 0) {
      currentPage += serializeListPage(list, currentItems.join(''), currentListStart);
    }

    return true;
  };

  const appendMeasuredElement = async (element: Element): Promise<boolean> => {
    const tagName = element.tagName.toLowerCase();
    if (tagName === 'ul' || tagName === 'ol') {
      return appendMeasuredList(element);
    }

    const wholeElement = element.outerHTML;
    const wholeElementFits = await fits(`${currentPage}${wholeElement}`);
    if (wholeElementFits === null) {
      return false;
    }
    if (wholeElementFits) {
      currentPage += wholeElement;
      return true;
    }

    const boundaries = getWordBoundaries(element);
    if (boundaries.length === 0) {
      if (!currentPage) {
        return false;
      }
      pushCurrentPage();
      return appendMeasuredElement(element);
    }

    let nextWordIndex = 0;
    while (nextWordIndex < boundaries.length) {
      const fragment = await findLargestMeasuredFragment(element, boundaries, nextWordIndex, currentPage, fits);
      if (fragment === null) {
        return false;
      }

      if (!fragment) {
        if (!currentPage) {
          return false;
        }
        pushCurrentPage();
        continue;
      }

      currentPage += fragment.html;
      nextWordIndex = fragment.nextWordIndex;
      if (nextWordIndex < boundaries.length) {
        pushCurrentPage();
      }
    }

    return true;
  };

  for (const element of elements) {
    const appended = await appendMeasuredElement(element);
    if (!appended) {
      return null;
    }
  }

  pushCurrentPage();
  return pages;
}

async function findLargestMeasuredFragment(
  element: Element,
  boundaries: Array<{ word: string; previous: RangeBoundary; current: RangeBoundary }>,
  firstWordIndex: number,
  currentPage: string,
  fits: (html: string) => Promise<boolean | null>,
  formatFragment: (html: string) => string = fragment => fragment
): Promise<{ html: string; nextWordIndex: number } | undefined | null> {
  let lowerBound = firstWordIndex;
  let upperBound = boundaries.length - 1;
  let largestFragment: { html: string; nextWordIndex: number } | undefined;

  while (lowerBound <= upperBound) {
    const candidateIndex = Math.floor((lowerBound + upperBound) / 2);
    const candidateEnd = candidateIndex === boundaries.length - 1
      ? { node: element, offset: element.childNodes.length }
      : boundaries[candidateIndex + 1].previous;
    const candidateHtml = formatFragment(serializeMeasuredRange(element, getRangeStart(element, boundaries, firstWordIndex), candidateEnd));
    const candidateFits = await fits(`${currentPage}${candidateHtml}`);
    if (candidateFits === null) {
      return null;
    }

    if (candidateFits) {
      largestFragment = { html: candidateHtml, nextWordIndex: candidateIndex + 1 };
      lowerBound = candidateIndex + 1;
    } else {
      upperBound = candidateIndex - 1;
    }
  }

  return largestFragment;
}

function getRangeStart(
  element: Element,
  boundaries: Array<{ word: string; previous: RangeBoundary; current: RangeBoundary }>,
  firstWordIndex: number
): RangeBoundary {
  return firstWordIndex === 0
    ? { node: element, offset: 0 }
    : boundaries[firstWordIndex].previous;
}

function serializeMeasuredRange(element: Element, start: RangeBoundary, end: RangeBoundary): string {
  const html = serializeRange(element, start, end);
  if (element.tagName.toLowerCase() !== 'ol') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  const orderedList = container.firstElementChild;
  const firstListItem = getTopLevelListItem(element, start.node);
  if (orderedList) {
    removeEmptyTopLevelListItems(orderedList);
  }
  if (orderedList && firstListItem) {
    orderedList.setAttribute('start', String(getOrderedListItemNumber(element, firstListItem)));
  }

  return container.innerHTML;
}

function removeEmptyTopLevelListItems(list: Element): void {
  Array.from(list.children)
    .filter(child => child.tagName.toLowerCase() === 'li')
    .filter(item => !(item.textContent || '').trim() && !item.querySelector('img, hr, br'))
    .forEach(item => item.remove());
}

function getTopLevelListItem(list: Element, node: Node): Element | null {
  let current: Node | null = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (current && current !== list) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as Element;
      if (element.tagName.toLowerCase() === 'li' && element.parentElement === list) {
        return element;
      }
    }
    current = current.parentNode;
  }

  return null;
}

function getOrderedListItemNumber(list: Element, target: Element): number {
  let nextNumber = getListStart(list);
  for (const listItem of Array.from(list.children).filter(child => child.tagName.toLowerCase() === 'li')) {
    const itemNumber = getListItemNumber(listItem, nextNumber);
    if (listItem === target) {
      return itemNumber;
    }
    nextNumber = itemNumber + 1;
  }

  return nextNumber;
}

function getInitialLinesCapacity(initialLinesCapacity: number | undefined): number {
  return Number.isFinite(initialLinesCapacity) && initialLinesCapacity! >= 1
    ? Math.floor(initialLinesCapacity!)
    : CONDITIONS_LINES_PER_PAGE;
}

function getUsableTerms(terms: string | null | undefined): string {
  if (!terms) {
    return '';
  }

  const normalizedTerms = terms.replace(/&nbsp;|\u00a0/g, ' ');
  const container = document.createElement('div');
  container.innerHTML = normalizedTerms;

  return (container.textContent || '').trim() ? normalizedTerms : '';
}

function splitPlainText(text: string, initialLinesCapacity: number): string[] {
  return splitWords(text, initialLinesCapacity * CONDITIONS_CHARS_PER_LINE, CONDITIONS_CHARS_PER_PAGE);
}

function splitHtmlElements(elements: Element[], initialLinesCapacity: number): string[] {
  const pages: string[] = [];
  let currentPage = '';
  let currentLines = 0;

  const currentPageCapacity = (): number => pages.length === 0
    ? initialLinesCapacity
    : CONDITIONS_LINES_PER_PAGE;

  const pushCurrentPage = (): void => {
    if (currentPage.trim()) {
      pages.push(currentPage);
    }
    currentPage = '';
    currentLines = 0;
  };

  const addFragment = (fragment: string): void => {
    const fragmentLines = estimateHtmlLines(fragment);
    if (currentLines > 0 && currentLines + fragmentLines > currentPageCapacity()) {
      pushCurrentPage();
    }

    currentPage += fragment;
    currentLines += fragmentLines;

    if (currentLines >= currentPageCapacity()) {
      pushCurrentPage();
    }
  };

  for (const element of elements) {
    const remainingLines = Math.max(1, currentPageCapacity() - currentLines);
    let fragments = splitElement(element, remainingLines, CONDITIONS_LINES_PER_PAGE);

    if (currentPage && estimateHtmlLines(fragments[0]) > remainingLines) {
      pushCurrentPage();
      fragments = splitElement(element, CONDITIONS_LINES_PER_PAGE, CONDITIONS_LINES_PER_PAGE);
    }

    for (const fragment of fragments) {
      addFragment(fragment);
    }
  }

  pushCurrentPage();
  return pages;
}

function splitElement(
  element: Element,
  initialMaximumLines: number,
  subsequentMaximumLines: number
): string[] {
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'ul' || tagName === 'ol') {
    return splitList(element, initialMaximumLines, subsequentMaximumLines);
  }

  return estimateElementLines(element) > initialMaximumLines
    ? splitLongElement(
      element,
      Math.max(1, initialMaximumLines - 1),
      Math.max(1, subsequentMaximumLines - 1)
    )
    : [element.outerHTML];
}

function splitList(list: Element, initialMaximumLines: number, subsequentMaximumLines: number): string[] {
  const listItems = Array.from(list.children).filter(child => child.tagName.toLowerCase() === 'li');
  if (listItems.length === 0) {
    return [list.outerHTML];
  }

  const pages: string[] = [];
  let currentItems: string[] = [];
  let currentLines = 1;
  let nextListNumber = getListStart(list);
  let currentListStart = nextListNumber;
  const currentPageCapacity = (): number => pages.length === 0
    ? initialMaximumLines
    : subsequentMaximumLines;

  const pushListPage = (): void => {
    if (currentItems.length > 0) {
      pages.push(serializeListPage(list, currentItems.join(''), currentListStart));
    }
    currentItems = [];
    currentLines = 1;
    currentListStart = nextListNumber;
  };

  for (const listItem of listItems) {
    const itemNumber = getListItemNumber(listItem, nextListNumber);
    nextListNumber = itemNumber + 1;
    const maximumListItemLines = Math.max(1, currentPageCapacity() - 2);
    const itemFragments = estimateElementLines(listItem) > maximumListItemLines
      ? splitLongElement(listItem, maximumListItemLines, Math.max(1, subsequentMaximumLines - 1))
      : [listItem.outerHTML];

    for (const itemFragment of itemFragments) {
      const itemLines = estimateHtmlLines(itemFragment);
      if (currentItems.length > 0 && currentLines + itemLines > currentPageCapacity()) {
        pushListPage();
      }

      if (currentItems.length === 0) {
        currentListStart = itemNumber;
      }

      currentItems.push(itemFragment);
      currentLines += itemLines;

      if (currentLines >= currentPageCapacity()) {
        pushListPage();
      }
    }
  }

  pushListPage();
  return pages;
}

function splitLongElement(
  element: Element,
  initialMaximumLines: number,
  subsequentMaximumLines: number
): string[] {
  const boundaries = getWordBoundaries(element);
  if (boundaries.length === 0) {
    return [element.outerHTML];
  }

  const fragments: string[] = [];
  let rangeStart: RangeBoundary = { node: element, offset: 0 };
  let currentLength = 0;

  for (const boundary of boundaries) {
    const wordLength = boundary.word.length + (currentLength > 0 ? 1 : 0);
    const maximumCharacters = (fragments.length === 0
      ? initialMaximumLines
      : subsequentMaximumLines) * CONDITIONS_CHARS_PER_LINE;
    if (currentLength > 0 && currentLength + wordLength > maximumCharacters) {
      fragments.push(serializeRange(element, rangeStart, boundary.previous));
      rangeStart = boundary.previous;
      currentLength = 0;
    }
    currentLength += wordLength;
  }

  const end: RangeBoundary = { node: element, offset: element.childNodes.length };
  fragments.push(serializeRange(element, rangeStart, end));

  return fragments.filter(fragment => getVisibleText(fragment).trim().length > 0 || fragment.includes('<br'));
}

function getWordBoundaries(element: Element): Array<{ word: string; previous: RangeBoundary; current: RangeBoundary }> {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const boundaries: Array<{ word: string; previous: RangeBoundary; current: RangeBoundary }> = [];
  let node: Text | null;

  while ((node = walker.nextNode() as Text | null)) {
    const matches = Array.from(node.data.matchAll(/\S+/g));
    for (const match of matches) {
      const start = match.index || 0;
      const end = start + match[0].length;
      boundaries.push({
        word: match[0],
        previous: { node, offset: start },
        current: { node, offset: end },
      });
    }
  }

  return boundaries;
}

function serializeRange(element: Element, start: RangeBoundary, end: RangeBoundary): string {
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  const wrapper = element.cloneNode(false) as Element;
  const contents = range.cloneContents();

  if (start.node === end.node && start.node.parentElement && start.node.parentElement !== element) {
    let nestedWrapper = start.node.parentElement.cloneNode(false) as Element;
    nestedWrapper.appendChild(contents);

    for (let parent = start.node.parentElement.parentElement; parent && parent !== element; parent = parent.parentElement) {
      const ancestor = parent.cloneNode(false) as Element;
      ancestor.appendChild(nestedWrapper);
      nestedWrapper = ancestor;
    }

    wrapper.appendChild(nestedWrapper);
  } else {
    wrapper.appendChild(contents);
  }
  return wrapper.outerHTML;
}

function serializeListPage(list: Element, children: string, start: number): string {
  const wrapper = list.cloneNode(false) as Element;
  if (wrapper.tagName.toLowerCase() === 'ol') {
    wrapper.setAttribute('start', String(start));
  }
  wrapper.innerHTML = children;
  return wrapper.outerHTML;
}

function getListStart(list: Element): number {
  return Number(list.getAttribute('start') || 1);
}

function getListItemNumber(listItem: Element, nextNumber: number): number {
  const explicitValue = listItem.getAttribute('value');
  return explicitValue === null ? nextNumber : Number(explicitValue);
}

function estimateElementLines(element: Element): number {
  const textLines = Math.max(1, Math.ceil((element.textContent || '').trim().length / CONDITIONS_CHARS_PER_LINE));
  const explicitBreaks = element.querySelectorAll('br').length;
  const listItems = element.tagName.toLowerCase() === 'ul' || element.tagName.toLowerCase() === 'ol'
    ? Array.from(element.children).filter(child => child.tagName.toLowerCase() === 'li').length
    : 0;

  return textLines + explicitBreaks + listItems + 1;
}

function estimateHtmlLines(html: string): number {
  const container = document.createElement('div');
  container.innerHTML = html;
  return Array.from(container.children).reduce((total, child) => total + estimateElementLines(child), 0);
}

function getVisibleText(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;
  return container.textContent || '';
}

function splitWords(text: string, initialMaxCharacters: number, maxCharacters: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const chunks: string[] = [];
  let currentChunk = '';

  for (const word of words) {
    const nextChunk = currentChunk ? `${currentChunk} ${word}` : word;
    const currentMaxCharacters = chunks.length === 0 ? initialMaxCharacters : maxCharacters;
    if (nextChunk.length > currentMaxCharacters && currentChunk) {
      chunks.push(currentChunk);
      currentChunk = word;
      continue;
    }
    currentChunk = nextChunk;
  }

  if (currentChunk) {
    chunks.push(currentChunk);
  }

  return chunks;
}

type RangeBoundary = {
  node: Node;
  offset: number;
};
